import React, { useState, useMemo, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import MoneyInput from './MoneyInput';
import AutoSizeTextarea from './AutoSizeTextarea';
import ConfirmDialog from './ConfirmDialog';
import OakStatusBadge from './compliance/OakStatusBadge';
import { CONFETTI_TOTAL_MS } from './confetti';
import { OttoAward } from './otto';
import { countAwardsByUser, isAwardMilestone, milestoneLabel } from '../lib/awardMilestones';
import {
  sendEmail,
  awardWinEmail,
  awardDeclineEmail,
  briefClosedEmail,
  briefAwardedInternalEmail,
  AWARD_DECLINE_DEFAULT_BODY,
  BRIEF_CLOSE_DEFAULT_BODY,
} from '../lib/email';
import { fmtDate } from '../lib/dates';
import { formatGBP as gbp } from '../lib/money';

// Completing a brief starts its auto-archive clock: now + the effective weeks
// (per-brief override if set, else the system-wide setting). The per-brief
// auto_archive_enabled flag + the system master toggle still gate whether the
// cron actually archives.
async function briefArchiveScheduledFor(overrideWeeks) {
  let weeks;
  const n = Number(overrideWeeks);
  if (Number.isFinite(n) && n > 0) {
    weeks = Math.max(1, Math.min(52, n));
  } else {
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'auto_archive_briefs_weeks').single();
    weeks = Math.max(1, parseInt(data?.value, 10) || 4);
  }
  return new Date(Date.now() + weeks * 7 * 86400000).toISOString();
}

// Inline panel that drives the entire close-brief flow — Award (Compare →
// Confirm → Done) or Complete Without Award (single page → Done). Owns its
// own form state; calls `onCompleted` after the success state plays so the
// parent can refetch and collapse.
//
// Visual reference: complete_brief_flow.html. Spec confirmed:
//   • compare stats row uses fields we actually have (Earliest Delivery /
//     Payment Schedule / Quote Type / Submitted)
//   • prices are NET ex-VAT, labelled accordingly
//   • RED-compliance suppliers render but are unselectable
//   • emails fan out on confirm; logged to supplier_email_log

const REASON_OPTIONS = [
  { key: 'cancelled',  label: 'Project cancelled by client' },
  { key: 'budget',     label: 'Budget pulled' },
  { key: 'no_suit',    label: 'No suitable responses' },
  { key: 'scope',      label: 'Scope changed — re-issuing' },
  { key: 'in_house',   label: 'Delivered in-house instead' },
  { key: 'other',      label: 'Other (please specify)' },
];

const REASON_LABEL_FOR = (key) => REASON_OPTIONS.find(o => o.key === key)?.label || '';

const formatGBP = (n) => gbp(n, { pence: 'auto', fallback: '£0' });
// Keep pence when present (a £66.65 quote stays £66.65), but don't force
// trailing zeros on whole-pound figures (£3,500,000 stays £3,500,000, not
// £3,500,000.00). Previously this rounded everything to whole pounds so
// the notifications list disagreed with the awarded amount header above.
const formatGBPShort = (n) => gbp(n, { pence: 'auto', fallback: '£0' });
// A missing value renders as "Not specified", never an em dash — a bare "—"
// reads as a rendering bug (brief §2/§6).
const dateOrNS = (d) => (d ? fmtDate(d) : 'Not specified');

const MONTHS_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const monthYear = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
};
// Working history is the thing an operator actually weighs against a price
// difference (brief §2) — shown instead of the internal "Uncategorised" gap.
const workingHistoryText = (meta) => {
  if (!meta || !meta.awardCount) return 'First time working with us';
  const times = meta.awardCount === 1 ? 'once' : `${meta.awardCount} times`;
  const last = meta.lastAwardAt ? ` · last in ${monthYear(meta.lastAwardAt)}` : '';
  return `Worked with us ${times}${last}`;
};

// Preset reason snippets for the not-awarded email (brief §4) — each inserts a
// real, considerate sentence so the body isn't left as "we've gone with someone
// else". Editable in Settings later; these are the built-in defaults.
const DECLINE_SNIPPETS = [
  { key: 'price',    label: 'Price',            text: 'On this occasion another supplier was a closer fit on price for this brief.' },
  { key: 'timing',   label: 'Timing',           text: 'This one came down to timing and availability across the dates involved.' },
  { key: 'scope',    label: 'Scope fit',        text: 'Another supplier was a closer fit for the specific scope of this brief.' },
  { key: 'existing', label: 'Existing supplier', text: 'We’ve gone with a supplier we already had lined up for this piece of work.' },
  { key: 'client',   label: 'Client’s choice',  text: 'The final decision on this one sat with our client.' },
];

// Soft risky-content check (brief §4): these emails go to people who bid against
// each other, so flag a price/figure or another supplier's name — but never
// block. Returns human-readable reasons.
const riskyContentReasons = (text, otherNames = []) => {
  const reasons = [];
  const t = String(text || '');
  if (/£\s?\d|\b\d[\d,]{2,}\b|\bvat\b/i.test(t)) reasons.push('a price or figure');
  const lower = t.toLowerCase();
  for (const n of otherNames) {
    if (n && n.trim().length > 2 && lower.includes(n.toLowerCase())) { reasons.push(`another supplier’s name (“${n.trim()}”)`); break; }
  }
  return reasons;
};
const firstName = (s) => (s ? String(s).trim().split(/\s+/)[0] : '');
const truncate = (s, n) => {
  const str = (s || '').toString().trim();
  if (str.length <= n) return str;
  return str.slice(0, n - 1).trimEnd() + '…';
};

export default function CompleteBriefPanel({
  brief,
  recipients,
  responses,
  currentInternalUser,
  initialSupplierId,
  initialStep,
  initialDecisionReason,
  onClose,
  onCompleted,
}) {
  // Submitted responses are the only candidates for award. Keep this list
  // narrowed once so every sub-step works against the same set.
  const respondingPairs = useMemo(() => {
    const out = [];
    for (const r of recipients) {
      if (r.status !== 'responded') continue;
      const response = responses.find(rr => rr.supplier_id === r.supplier_id && rr.is_submitted !== false);
      if (!response) continue;
      out.push({ recipient: r, response });
    }
    return out;
  }, [recipients, responses]);

  // Close-without-award notifications include every non-withdrawn,
  // non-declined recipient — responders AND people who never replied
  // ("the brief is closed, thanks for your time"). Award keeps the
  // narrower responders-only scope since you can't award to a no-show.
  // Record-only suppliers are filtered out by the picker UI directly.
  const closeNotifyPairs = useMemo(() => {
    return recipients
      .filter(r => r.status !== 'withdrawn' && r.status !== 'declined')
      .map(r => ({ recipient: r, response: responses.find(rr => rr.supplier_id === r.supplier_id && rr.is_submitted !== false) || null }));
  }, [recipients, responses]);

  const hasResponses = respondingPairs.length > 0;
  // Flow branches on the COUNT of responders, never on a control the user
  // picks (brief §1): exactly one → a single screen with the supplier
  // pre-selected (no compare, no sort, no superlatives); zero → close-only;
  // two+ → the compare → confirm two-step.
  const responded = respondingPairs.length;
  const singleResponse = responded === 1;
  const noResponses = responded === 0;

  const [mode, setMode] = useState(hasResponses ? 'award' : 'complete');
  // initialStep / initialSupplierId allow the panel to open at Step 2
  // with a supplier already chosen — used by the side-by-side
  // comparison view's "Award to X" footer buttons. The seed only
  // runs at first mount; the user can navigate back to Compare from
  // Step 2 if they want to change suppliers.
  const [step, setStep] = useState(initialStep || 'compare');
  const [selectedSupplierId, setSelectedSupplierId] = useState(initialSupplierId || null);
  const [awardAmount, setAwardAmount] = useState(() => {
    if (!initialSupplierId) return '';
    const pair = (recipients || []).find(r => r.supplier_id === initialSupplierId && r.status === 'responded');
    if (!pair) return '';
    const resp = (responses || []).find(rr => rr.supplier_id === initialSupplierId && rr.is_submitted !== false);
    return resp?.total_price != null ? String(resp.total_price) : '';
  });
  // Seeded from the compare screen's working note for this supplier when
  // present (quote-comparison §7) — a starting point, still fully editable.
  const [decisionReason, setDecisionReason] = useState(initialDecisionReason || '');
  const [winnerMessage, setWinnerMessage] = useState('');
  // Not-awarded email editing (brief §4). notSelectedAll is the all-suppliers
  // body (seeded from the default); notSelectedOverrides holds per-supplier
  // bodies keyed by supplier_id; notSelectedTab is the active editor tab
  // ('all' or a supplier_id). Resolution on send: override → all → default.
  const [notSelectedAll, setNotSelectedAll] = useState(AWARD_DECLINE_DEFAULT_BODY);
  const [notSelectedOverrides, setNotSelectedOverrides] = useState({});
  const [notSelectedTab, setNotSelectedTab] = useState('all');
  const [sortBy, setSortBy] = useState('price');

  const [completeReason, setCompleteReason] = useState(null);
  const [completeReasonOther, setCompleteReasonOther] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [closedMessage, setClosedMessage] = useState('');

  // Per-responder context loaded on open (brief §2): prior working history
  // (award count + last award) and a read-only compliance flag (documents
  // expiring soon / expired). Display only — never blocks the award.
  const [supplierMeta, setSupplierMeta] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showDone, setShowDone] = useState(false);
  // Tracks whether the last submit (award or close) sent notifications,
  // so the success pane can phrase itself correctly ("notified" vs "not
  // notified yet"). Defaults true to match the prior single-button
  // behaviour on both flows.
  const [lastActionNotified, setLastActionNotified] = useState(true);
  // Award celebration runs on award success (Captain Otto + wing-tip
  // confetti; see OttoAward). The old click-origin ConfettiBurst was
  // dropped so Otto's confetti is the single source.
  // 1.5s total animation; the peek component owns its own internal
  // timing and calls onComplete when done so we can reset showPeek
  // for the next award.
  const [showPeek, setShowPeek] = useState(false);
  // Milestone label for the Captain-Otto award celebration (null on a
  // normal award — Captain Otto still fires, just without the flourish).
  const [milestoneMsg, setMilestoneMsg] = useState(null);

  const selectedPair = respondingPairs.find(p => p.recipient.supplier_id === selectedSupplierId) || null;
  const isWinnerRed = selectedPair?.recipient?.suppliers?.compliance_status === 'red';

  // Default winner message — prefilled once the user picks a supplier and
  // hasn't yet typed anything of their own. Editing it sticks; clearing it
  // doesn't get auto-refilled (the user wanted blank).
  const [winnerMessageTouched, setWinnerMessageTouched] = useState(false);

  // Per-supplier email selection — defaults all-on so today's behaviour
  // is unchanged for an admin who doesn't open the toggles. Sync with
  // respondingPairs so newly-added respondents (rare, mid-flight)
  // default to ticked rather than silently excluded. Set of
  // brief_recipients.id; the same set governs both Award and Close
  // notifications since they share the same rows.
  const [emailRecipientIds, setEmailRecipientIds] = useState(() => new Set());
  // Mode-aware notify scope. Award flow only emails responders;
  // close-without-award offers every non-declined recipient. Responders
  // default ticked in both modes (they engaged — they expect a follow-
  // up). Non-responders default UNTICKED on close (don't spam people
  // who never replied unless the operator opts them in deliberately).
  const notifyPairs = mode === 'award' ? respondingPairs : closeNotifyPairs;
  useEffect(() => {
    setEmailRecipientIds(prev => {
      const next = new Set(prev);
      let changed = false;
      const liveIds = new Set(notifyPairs.map(p => p.recipient.id));
      for (const { recipient } of notifyPairs) {
        // Auto-tick on first appearance only — respect the operator's
        // un-tick after that. Responders default in; non-responders on
        // close default out.
        if (!next.has(recipient.id) && !prev.has(recipient.id)) {
          const isResponder = recipient.status === 'responded';
          if (isResponder) { next.add(recipient.id); changed = true; }
        }
      }
      for (const id of Array.from(next)) {
        if (!liveIds.has(id)) { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [notifyPairs]);
  const toggleEmailRecipient = (recipientId) => {
    setEmailRecipientIds(prev => {
      const next = new Set(prev);
      if (next.has(recipientId)) next.delete(recipientId); else next.add(recipientId);
      return next;
    });
  };
  // Select-all / clear-all shortcuts on the Notifications card. The clear
  // path is the new home for the old "Award/Close without notifying" button
  // — un-ticking everyone is the same outcome, with the per-supplier picker
  // already in view so the operator can also keep a subset if they want.
  // Works against the mode-active notify scope so the buttons feel
  // consistent whether you're awarding (responders only) or closing
  // (every non-declined recipient).
  const selectableRecipients = notifyPairs.filter(p => !p.recipient.suppliers?.is_record_only && p.recipient.suppliers?.status !== 'inactive');
  const selectAllEmails = () => setEmailRecipientIds(new Set(selectableRecipients.map(p => p.recipient.id)));
  const clearAllEmails = () => setEmailRecipientIds(new Set());
  const tickedEmailCount = emailRecipientIds.size;
  useEffect(() => {
    if (!selectedPair || winnerMessageTouched) return;
    const winnerFirst = firstName(selectedPair.recipient.suppliers?.main_contact_name) || 'team';
    const meFirst = firstName(currentInternalUser?.full_name) || 'the team';
    setWinnerMessage(
      `Hi ${winnerFirst} — congratulations, your quote stood out and we'd like to award you this brief. ` +
      `Looking forward to working with you on it. I'll be in touch this week with next steps.\n\n— ${meFirst}`
    );
  }, [selectedPair, winnerMessageTouched, currentInternalUser?.full_name]);

  // Load working history + compliance flag for the responders (brief §2).
  // Best-effort: a query hiccup just leaves the card without the extra context.
  useEffect(() => {
    const ids = Array.from(new Set(respondingPairs.map(p => p.recipient.supplier_id).filter(Boolean)));
    if (ids.length === 0) { setSupplierMeta({}); return; }
    let cancelled = false;
    (async () => {
      const meta = {};
      ids.forEach(id => { meta[id] = { awardCount: 0, lastAwardAt: null, expiringCount: 0 }; });
      // Prior awards to these suppliers (excluding this brief).
      const { data: awarded } = await supabase
        .from('briefs')
        .select('awarded_supplier_id, closed_at')
        .in('awarded_supplier_id', ids)
        .neq('id', brief.id);
      for (const b of (awarded || [])) {
        const m = meta[b.awarded_supplier_id];
        if (!m) continue;
        m.awardCount += 1;
        if (b.closed_at && (!m.lastAwardAt || new Date(b.closed_at) > new Date(m.lastAwardAt))) m.lastAwardAt = b.closed_at;
      }
      // Compliance flag: required docs expiring within 30 days or already
      // expired. Read-only signal — awarding a supplier whose insurance
      // lapses next week is exactly the mistake this screen should surface.
      const horizon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
      const { data: docs } = await supabase
        .from('supplier_onboarding_documents')
        .select('supplier_id, expiry_date, not_required')
        .in('supplier_id', ids);
      for (const d of (docs || [])) {
        const m = meta[d.supplier_id];
        if (!m || d.not_required) continue;
        if (d.expiry_date && d.expiry_date <= horizon) m.expiringCount += 1;
      }
      if (!cancelled) setSupplierMeta(meta);
    })();
    return () => { cancelled = true; };
  }, [respondingPairs, brief.id]);

  // Single-response flow (brief §1): pre-select the one responder and drop
  // straight onto the confirm screen — there's nothing to compare. Skip a
  // Red-compliance sole responder (can't be awarded); they fall through to
  // the normal (empty) confirm guard and the operator closes without award.
  useEffect(() => {
    if (!singleResponse || mode !== 'award' || selectedSupplierId) return;
    const only = respondingPairs[0];
    if (only.recipient.suppliers?.compliance_status === 'red') return;
    setSelectedSupplierId(only.recipient.supplier_id);
    if (only.response.total_price != null) setAwardAmount(String(only.response.total_price));
    setStep('confirm');
  }, [singleResponse, mode, selectedSupplierId, respondingPairs]);

  // Sorted compare list. Cheapest is tagged on the card regardless of sort.
  const sortedPairs = useMemo(() => {
    const list = [...respondingPairs];
    if (sortBy === 'price') {
      list.sort((a, b) => (Number(a.response.total_price) || Infinity) - (Number(b.response.total_price) || Infinity));
    } else if (sortBy === 'submitted') {
      list.sort((a, b) => new Date(a.response.submitted_at || a.response.created_at || 0) - new Date(b.response.submitted_at || b.response.created_at || 0));
    } else if (sortBy === 'name') {
      list.sort((a, b) => (a.recipient.suppliers?.supplier_name || '').localeCompare(b.recipient.suppliers?.supplier_name || ''));
    }
    return list;
  }, [respondingPairs, sortBy]);

  const cheapestSupplierId = useMemo(() => {
    let lowest = null;
    for (const { recipient, response } of respondingPairs) {
      const n = Number(response.total_price);
      if (!isFinite(n)) continue;
      if (lowest == null || n < lowest.value) lowest = { value: n, id: recipient.supplier_id };
    }
    return lowest?.id || null;
  }, [respondingPairs]);

  const priceMin = useMemo(() => {
    const nums = respondingPairs.map(p => Number(p.response.total_price)).filter(n => isFinite(n));
    return nums.length ? Math.min(...nums) : null;
  }, [respondingPairs]);
  const priceMax = useMemo(() => {
    const nums = respondingPairs.map(p => Number(p.response.total_price)).filter(n => isFinite(n));
    return nums.length ? Math.max(...nums) : null;
  }, [respondingPairs]);
  const cheapestDelta = useMemo(() => {
    if (!selectedPair || selectedPair.recipient.supplier_id !== cheapestSupplierId) return null;
    const own = Number(selectedPair.response.total_price);
    if (!isFinite(own)) return null;
    const others = respondingPairs
      .filter(p => p.recipient.supplier_id !== cheapestSupplierId)
      .map(p => Number(p.response.total_price))
      .filter(n => isFinite(n));
    if (others.length === 0) return null;
    return Math.min(...others) - own;
  }, [selectedPair, cheapestSupplierId, respondingPairs]);

  // All quotes identical → no "lowest price" superlative (brief §2 edge case).
  const hasPriceSpread = priceMin != null && priceMax != null && priceMin !== priceMax;

  // Awarded-vs-quoted delta (brief §3): the number a finance review asks about.
  // Positive = above quote, negative = below.
  const quotedForWinner = selectedPair?.response?.total_price;
  const awardDelta = (() => {
    if (quotedForWinner == null || awardAmount === '' || isNaN(Number(awardAmount))) return null;
    const d = Number(awardAmount) - Number(quotedForWinner);
    return d === 0 ? null : d;
  })();

  // Invited suppliers who never responded — listed greyed + disabled in the
  // award Notifications group so "N will be emailed" reconciles against the
  // total invited (brief §3). Never emailed on award.
  const awardNonResponders = useMemo(() => (
    recipients.filter(r =>
      r.status !== 'responded' && r.status !== 'declined' && r.status !== 'withdrawn'
      && !r.suppliers?.is_record_only && r.suppliers?.status !== 'inactive')
  ), [recipients]);

  // ---- Not-awarded email editing (brief §4) --------------------------------
  const notSelectedResponders = respondingPairs.filter(p => p.recipient.supplier_id !== selectedSupplierId);
  const allResponderNames = respondingPairs.map(p => p.recipient.suppliers?.supplier_name).filter(Boolean);
  // The body a given not-selected supplier actually receives: their override,
  // else the all-suppliers body (never the winner's data — §5.3).
  const resolveNotSelectedBody = (supplierId) =>
    (notSelectedOverrides[supplierId] != null ? notSelectedOverrides[supplierId] : notSelectedAll);
  const activeSupplierName = notSelectedTab === 'all'
    ? null
    : (respondingPairs.find(p => p.recipient.supplier_id === notSelectedTab)?.recipient.suppliers?.supplier_name || null);
  const activeNotSelectedBody = notSelectedTab === 'all' ? notSelectedAll : resolveNotSelectedBody(notSelectedTab);
  const setActiveNotSelectedBody = (val) => {
    if (notSelectedTab === 'all') setNotSelectedAll(val);
    else setNotSelectedOverrides(prev => ({ ...prev, [notSelectedTab]: val }));
  };
  const appendSnippet = (text) => {
    const cur = activeNotSelectedBody || '';
    const sep = cur && !/\s$/.test(cur) ? ' ' : '';
    setActiveNotSelectedBody((cur + sep + text).trim());
  };
  const resetActiveNotSelected = () => {
    if (notSelectedTab === 'all') setNotSelectedAll(AWARD_DECLINE_DEFAULT_BODY);
    else setNotSelectedOverrides(prev => { const n = { ...prev }; delete n[notSelectedTab]; return n; });
  };
  const activeOverridden = notSelectedTab !== 'all' && notSelectedOverrides[notSelectedTab] != null;
  // Flag another supplier's name — for a per-supplier tab that supplier's own
  // name is fine, only the others (incl the winner) are risky.
  const riskyNamesForActive = notSelectedTab === 'all'
    ? allResponderNames
    : allResponderNames.filter(n => n !== activeSupplierName);
  const activeRisky = riskyContentReasons(activeNotSelectedBody, riskyNamesForActive);

  const awardDirty = () =>
    Boolean(selectedSupplierId || awardAmount || decisionReason || winnerMessageTouched
      || Object.keys(notSelectedOverrides).length > 0 || notSelectedAll !== AWARD_DECLINE_DEFAULT_BODY);
  const completeDirty = () =>
    Boolean(completeReason || completeReasonOther || completeNotes || closedMessage);

  const handleSwitchMode = (next) => {
    if (next === mode) return;
    const dirty = mode === 'award' ? awardDirty() : completeDirty();
    if (dirty && !window.confirm('Switch modes? Your input will be discarded.')) return;
    setError(null);
    if (next === 'award') {
      setMode('award');
      setStep('compare');
      setCompleteReason(null);
      setCompleteReasonOther('');
      setCompleteNotes('');
      setClosedMessage('');
    } else {
      setMode('complete');
      setSelectedSupplierId(null);
      setAwardAmount('');
      setDecisionReason('');
      setWinnerMessage('');
      setWinnerMessageTouched(false);
      setNotSelectedAll(AWARD_DECLINE_DEFAULT_BODY);
      setNotSelectedOverrides({});
      setNotSelectedTab('all');
    }
  };

  const handleSelectSupplier = (supplierId) => {
    const pair = respondingPairs.find(p => p.recipient.supplier_id === supplierId);
    if (!pair) return;
    if (pair.recipient.suppliers?.compliance_status === 'red') return;
    setSelectedSupplierId(supplierId);
    const prefill = pair.response.total_price;
    if (prefill != null) setAwardAmount(String(prefill));
  };

  // Radiogroup keyboard support (brief §7): arrow keys move focus AND
  // selection across the selectable (non-Red) quote cards, wrapping at the
  // ends. Red cards are disabled, so they never receive this handler.
  const handleCardKeyDown = (e) => {
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) return;
    e.preventDefault();
    const group = e.currentTarget.closest('[role="radiogroup"]');
    if (!group) return;
    const radios = Array.from(group.querySelectorAll('[role="radio"]'))
      .filter(el => el.getAttribute('aria-disabled') !== 'true');
    if (radios.length === 0) return;
    let idx = radios.indexOf(e.currentTarget);
    if (idx === -1) idx = 0;
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const next = radios[(idx + (forward ? 1 : -1) + radios.length) % radios.length];
    next.focus();
    next.click();
  };

  const fanOutEmails = async (winnerSupplierId, awardedNumericAmount, meta = {}) => {
    const failures = [];
    const portalUrl = `${window.location.origin}/portal`;
    const templateVersion = meta.templateVersion ?? null;

    // Award fan-out stays scoped to responders; close fan-out widens
    // to every non-declined recipient so a "the brief is closed,
    // thanks for your time" can land in non-responders' inboxes if
    // the operator deliberately ticks them.
    const fanOutPairs = mode === 'award' ? respondingPairs : closeNotifyPairs;
    for (const { recipient } of fanOutPairs) {
      if (recipient.suppliers?.is_record_only || recipient.suppliers?.status === 'inactive') continue;
      // Admin un-ticked this supplier on the Notifications card — skip
      // their email entirely. The brief still moves to its target
      // state (Awarded / Closed) and the audit row is written; the
      // admin is signalling they'll reach out off-platform.
      if (!emailRecipientIds.has(recipient.id)) continue;
      const isWinner = winnerSupplierId && recipient.supplier_id === winnerSupplierId;
      const recipientEmails = [];

      const sendOne = async (to, contactName) => {
        if (mode === 'award' && isWinner) {
          const { subject, html } = awardWinEmail({
            contactName,
            supplierName: recipient.suppliers?.supplier_name,
            briefName: brief.brief_name,
            awardedAmount: awardedNumericAmount,
            personalMessage: winnerMessage,
            briefUrl: portalUrl,
          });
          try { await sendEmail({ to, subject, html }); recipientEmails.push(to); }
          catch { failures.push(`${recipient.suppliers?.supplier_name || 'supplier'} → ${to}`); }
        } else if (mode === 'award') {
          // Per-supplier not-awarded body (override → all → default). Only this
          // supplier's own data reaches the template — never the winner's (§5.3).
          const { subject, html } = awardDeclineEmail({
            contactName,
            supplierName: recipient.suppliers?.supplier_name,
            briefName: brief.brief_name,
            body: resolveNotSelectedBody(recipient.supplier_id),
            briefUrl: portalUrl,
          });
          try { await sendEmail({ to, subject, html }); recipientEmails.push(to); }
          catch { failures.push(`${recipient.suppliers?.supplier_name || 'supplier'} → ${to}`); }
        } else {
          const { subject, html } = briefClosedEmail({
            contactName,
            supplierName: recipient.suppliers?.supplier_name,
            briefName: brief.brief_name,
            personalMessage: closedMessage,
            briefUrl: portalUrl,
          });
          try { await sendEmail({ to, subject, html }); recipientEmails.push(to); }
          catch { failures.push(`${recipient.suppliers?.supplier_name || 'supplier'} → ${to}`); }
        }
      };

      // Strict portal-user routing — type='portal_user' only. Legacy
      // 'primary'/'secondary' entries are skipped so award win / award
      // decline / brief closed emails never silently route to
      // main_contact_email. Operators sync legacy rows from the brief
      // detail's recipients banner before closing the brief.
      const selected = (Array.isArray(recipient.selected_contacts) ? recipient.selected_contacts : [])
        .filter(c => c.selected && c.email && c.type === 'portal_user');
      for (const c of selected) await sendOne(c.email, c.name);
      const extras = recipient.additional_emails
        ? recipient.additional_emails.split(';').map(e => e.trim()).filter(Boolean)
        : [];
      for (const email of extras) await sendOne(email, '');

      // Surface a supplier that WAS meant to be emailed but has no deliverable
      // address (no portal-user contact + no extras). Previously this silently
      // sent nothing with no failure — so the award "didn't trigger" with zero
      // feedback. Now it reports, pointing at the fix (Sync to portal users).
      if (selected.length === 0 && extras.length === 0) {
        failures.push(`${recipient.suppliers?.supplier_name || 'supplier'} → no portal-user recipients — use “Sync to portal users” on the recipient row`);
      }

      if (recipientEmails.length > 0) {
        const kind = mode === 'award'
          ? (isWinner ? 'brief_award_win' : 'brief_award_decline')
          : 'brief_closed';
        // Per-send traceability (§5.3): the template version in play and whether
        // this supplier's body was overridden from the standard wording.
        const bodyOverridden = mode !== 'award'
          ? false
          : isWinner
            ? !!(winnerMessage && winnerMessage.trim())
            : (notSelectedOverrides[recipient.supplier_id] != null || notSelectedAll !== AWARD_DECLINE_DEFAULT_BODY);
        await supabase.from('supplier_email_log').insert([{
          supplier_id: recipient.supplier_id,
          email_kind: kind,
          recipients: recipientEmails,
          sent_by: currentInternalUser?.id || null,
          template_version: mode === 'award' ? templateVersion : null,
          body_overridden: bodyOverridden,
        }]);
      }
    }

    // Internal Tier-2 confirmation back to the awarder — only on a real
    // award (not on close-without-award) and only when notify is on, so
    // an admin who opts out of the supplier fan-out doesn't get a self-
    // email either. Not logged to supplier_email_log (that table is
    // keyed by supplier_id; this mail goes to an internal user).
    if (mode === 'award' && winnerSupplierId && currentInternalUser?.email) {
      const winnerPair = respondingPairs.find(p => p.recipient.supplier_id === winnerSupplierId);
      const briefUrlInternal = `${window.location.origin}/briefs/${brief.id}`;
      const eventDate = brief.event_live_date ? fmtDate(brief.event_live_date) : null;
      const { subject, html } = briefAwardedInternalEmail({
        awarderName: currentInternalUser.full_name,
        supplierName: winnerPair?.recipient.suppliers?.supplier_name,
        briefName: brief.brief_name,
        awardedAmount: awardedNumericAmount,
        eventDate,
        briefUrl: briefUrlInternal,
      });
      try { await sendEmail({ to: currentInternalUser.email, subject, html }); }
      catch { failures.push(`internal confirmation → ${currentInternalUser.email}`); }
    }

    return failures;
  };

  // Exclusive-brief award confirmation (2026-06): state-driven so we
  // can surface the brand-styled ConfirmDialog instead of
  // window.confirm. When the operator clicks Award on an exclusive
  // brief with extra recipients + notify on, we stash the click
  // context (we need event + notify for the deferred fire) and
  // open the dialog; Confirm calls performAward(event, { notify }).
  const [exclusiveAwardPending, setExclusiveAwardPending] = useState(null);
  // Fires once per award (idempotency guard, §5.3) — reset on a failed write.
  const awardFiredRef = useRef(false);

  // 30-second hold-then-send (brief §9.1): after pressing Award nothing is
  // written or emailed until the countdown elapses or the operator hits Send
  // now; Cancel aborts with zero side effects. `hold` carries the deferred
  // award context. The countdown + fire live in an effect after performAward.
  const [hold, setHold] = useState(null); // { seconds, notify, burstOrigin }
  const startAwardHold = (ctx) => { setError(null); setHold({ seconds: 30, ...ctx }); };
  const cancelHold = () => setHold(null);

  const handleSubmitAward = async (event, { notify }) => {
    if (!selectedSupplierId) { setError('Pick a supplier first.'); return; }
    if (isWinnerRed) { setError('This supplier cannot be awarded — Red compliance.'); return; }
    if (!awardAmount || isNaN(Number(awardAmount))) { setError('Enter a numeric awarded amount.'); return; }
    if (!decisionReason.trim()) { setError('Decision reason is required.'); return; }
    // Exclusive-brief award warning. notify=false skips the prompt
    // (no emails go out, so no risk of unwanted messaging). Count only the
    // suppliers who would ACTUALLY receive a "didn't win" email — i.e.
    // non-winner responders still ticked on the Notifications card. The
    // award fan-out is responders-only (fanOutEmails, mode==='award') and
    // skips anyone un-ticked, so non-responders / un-ticked suppliers can't
    // be emailed and shouldn't trigger (or inflate) this warning.
    const otherRecipients = respondingPairs.filter(({ recipient }) =>
      recipient.supplier_id !== selectedSupplierId
      && emailRecipientIds.has(recipient.id)
      && !(recipient.suppliers?.is_record_only || recipient.suppliers?.status === 'inactive')
    );
    if (brief?.brief_type === 'exclusive' && notify && otherRecipients.length > 0) {
      // Snapshot the button rect now (synchronous) so the confetti
      // burst origin survives the deferred fire after Confirm. The
      // event won't be reusable by the time the dialog resolves.
      const btnRect = event?.currentTarget?.getBoundingClientRect?.();
      const burstOrigin = btnRect
        ? { x: btnRect.left + btnRect.width / 2, y: btnRect.top + btnRect.height / 2 }
        : null;
      setExclusiveAwardPending({ notify, otherCount: otherRecipients.length, burstOrigin });
      return;
    }
    // Snapshot the clicked button's viewport position synchronously,
    // before any awaits — the panel may transition to its done state
    // and unmount the button before the burst fires.
    const btnRect = event?.currentTarget?.getBoundingClientRect?.();
    const burstOrigin = btnRect
      ? { x: btnRect.left + btnRect.width / 2, y: btnRect.top + btnRect.height / 2 }
      : null;
    startAwardHold({ notify, burstOrigin });
  };

  // Extracted award-write flow so both the direct-Award path and the
  // exclusive-brief-confirm path call exactly the same logic. Takes
  // the pre-captured notify + burstOrigin (the original event is
  // synchronously consumed for both).
  const performAward = async ({ notify, burstOrigin }) => {
    // Idempotency guard (brief §5.3): once the award has fired, ignore any
    // re-entry (a dev double-invoked effect, a stray Send-now + timer race) so
    // a double-fire can't double-write or double-send.
    if (awardFiredRef.current) return;
    awardFiredRef.current = true;
    setSubmitting(true);
    setError(null);
    // Which template wording is the base for this award (§5.3) — snapshot the
    // active not-selected version so a sent email traces back to it.
    let templateVersion = null;
    try {
      const { data: tpl } = await supabase.from('email_templates')
        .select('version').eq('kind', 'award_not_selected').eq('is_active', true).single();
      templateVersion = tpl?.version ?? null;
    } catch { /* traceability is best-effort */ }
    const archiveScheduledFor = await briefArchiveScheduledFor(brief?.auto_archive_weeks_override);
    const { error: updateError } = await supabase.from('briefs').update({
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_by: currentInternalUser?.id || null,
      award_decision_reason: decisionReason.trim(),
      awarded_supplier_id: selectedSupplierId,
      awarded_amount: Number(awardAmount),
      auto_archive_scheduled_for: archiveScheduledFor,
      // Per-award record (§5.2): the quote snapshot for the delta, the email
      // overrides so a sent message is reconstructable, and the template base.
      quoted_amount: quotedForWinner != null ? Number(quotedForWinner) : null,
      not_selected_body: notSelectedAll !== AWARD_DECLINE_DEFAULT_BODY ? notSelectedAll : null,
      not_selected_overrides: notSelectedOverrides || {},
      won_body: winnerMessage && winnerMessage.trim() ? winnerMessage.trim() : null,
      award_template_version: templateVersion,
    }).eq('id', brief.id);
    // Awarding or closing resolves any outstanding post-deadline update requests
    // with no separate action (respond-to-update §2/§4). Best-effort.
    supabase.from('brief_update_requests')
      .update({ resolved_at: new Date().toISOString() })
      .eq('brief_id', brief.id).is('resolved_at', null)
      .then(() => {});
    if (updateError) {
      setError(`Could not complete brief: ${updateError.message}`);
      setSubmitting(false);
      awardFiredRef.current = false; // let the operator retry
      return;
    }
    // Award is on the books — fire the burst now. The panel stays
    // on the confirm view until BOTH the confetti has finished AND
    // (when notify is on) the email fan-out has returned, so the
    // celebration doesn't get clipped mid-flight by the transition
    // to the done state. Email failures surface via the error
    // state below.
    // Milestone check — the awarding user's personal running total (this
    // award included). Every award fires Captain Otto; a milestone adds
    // the label. Non-fatal if the count query fails.
    let msg = null;
    try {
      const count = await countAwardsByUser(currentInternalUser?.id);
      if (isAwardMilestone(count)) msg = milestoneLabel(count);
    } catch (e) { /* celebration still fires without a label */ }
    setMilestoneMsg(msg);
    setShowPeek(true);
    setLastActionNotified(notify);
    const burstHold = new Promise(resolve => setTimeout(resolve, CONFETTI_TOTAL_MS));
    const fanOutTask = notify
      ? fanOutEmails(selectedSupplierId, Number(awardAmount), { templateVersion }).then(failures => {
          if (failures.length > 0) {
            setError(`Brief awarded, but ${failures.length} email(s) failed: ${failures.slice(0, 2).join('; ')}${failures.length > 2 ? '…' : ''}`);
          }
        })
      : Promise.resolve();
    await Promise.all([burstHold, fanOutTask]);
    setSubmitting(false);
    setShowDone(true);
  };

  // Hold countdown — ticks once a second; at zero (or via Send now) it fires
  // the deferred award. Placed after performAward so it references a defined
  // function. Cancel just clears `hold`, so nothing ever committed.
  const sendHoldNow = () => {
    if (!hold) return;
    const { notify, burstOrigin } = hold;
    setHold(null);
    performAward({ notify, burstOrigin });
  };
  useEffect(() => {
    if (!hold) return undefined;
    if (hold.seconds <= 0) {
      const { notify, burstOrigin } = hold;
      setHold(null);
      performAward({ notify, burstOrigin });
      return undefined;
    }
    const t = setTimeout(() => setHold(h => (h ? { ...h, seconds: h.seconds - 1 } : null)), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold]);

  const handleSubmitClose = async ({ notify } = { notify: true }) => {
    if (!completeReason) { setError('Pick a reason.'); return; }
    if (completeReason === 'other' && !completeReasonOther.trim()) { setError('Add a short note for "Other".'); return; }
    setSubmitting(true);
    setError(null);
    const reasonText = completeReason === 'other'
      ? `Other — ${completeReasonOther.trim()}`
      : REASON_LABEL_FOR(completeReason);
    const fullReason = completeNotes.trim()
      ? `${reasonText}\n\n${completeNotes.trim()}`
      : reasonText;
    const archiveScheduledFor = await briefArchiveScheduledFor(brief?.auto_archive_weeks_override);
    const { error: updateError } = await supabase.from('briefs').update({
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_by: currentInternalUser?.id || null,
      award_decision_reason: fullReason,
      awarded_supplier_id: null,
      awarded_amount: null,
      auto_archive_scheduled_for: archiveScheduledFor,
    }).eq('id', brief.id);
    if (updateError) {
      setError(`Could not close brief: ${updateError.message}`);
      setSubmitting(false);
      return;
    }
    setLastActionNotified(notify);
    if (notify) {
      const failures = await fanOutEmails(null, null);
      if (failures.length > 0) {
        setError(`Brief closed, but ${failures.length} email(s) failed: ${failures.slice(0, 2).join('; ')}${failures.length > 2 ? '…' : ''}`);
      }
    }
    setSubmitting(false);
    setShowDone(true);
  };

  // Transient success state — auto-close after ~3s so the permanent
  // outcome banner on BriefDetail becomes the canonical record. Errors
  // surfaced during submit pause the auto-dismiss so the user can read them.
  useEffect(() => {
    if (!showDone) return;
    if (error) return;
    const t = setTimeout(() => { onCompleted?.(); }, 3000);
    return () => clearTimeout(t);
  }, [showDone, error, onCompleted]);

  // ===== Render =====

  const accentStripe = (
    <span aria-hidden="true"
      className="absolute top-0 left-0 right-0 h-0.5"
      style={{ background: 'linear-gradient(90deg, #92D400 0%, #92D400 30%, transparent 30%)' }} />
  );

  // Header copy varies per state.
  const isAwardCompare = mode === 'award' && step === 'compare' && !showDone;
  const isAwardConfirm = mode === 'award' && step === 'confirm' && !showDone;
  const eyebrow = showDone
    ? (mode === 'award' ? 'Award Confirmed' : 'Brief Closed')
    // Single-response award is one screen, so no "Step X of 2" indicator.
    : (mode === 'award' && !singleResponse ? `Complete Brief · Step ${isAwardCompare ? '1' : '2'} of 2` : 'Complete Brief');
  const title = showDone
    ? (mode === 'award'
        ? `Brief awarded to ${selectedPair?.recipient?.suppliers?.supplier_name || 'supplier'}`
        : 'This brief has been closed')
    : isAwardCompare ? 'Compare responses and pick a winner'
      : isAwardConfirm ? 'Confirm the award and notify everyone'
        : mode === 'complete' ? 'Close this brief without awarding'
          : 'Pick a winning supplier or close the brief';
  const sub = showDone
    ? (mode === 'award'
        ? 'Pipeline updated and everyone has been told.'
        : 'Responding suppliers have been notified.')
    : isAwardCompare ? (hasResponses
        ? `Select one of the ${respondingPairs.length} supplier${respondingPairs.length === 1 ? '' : 's'} who responded. Sort to triage quickly.`
        : 'No supplier responses yet — switch modes to close without award.')
      : isAwardConfirm ? 'Adjust the amount, add an optional message, then send notifications.'
        : "Tell us why — and let the responding suppliers know.";

  return (
    <div className="relative bg-white border border-line rounded-xl overflow-hidden mb-6 font-['Metropolis']">
      {/* Award celebration — Captain Otto pops in and throws the confetti
          from his wing-tips (single source; the old ConfettiBurst was
          dropped). Portal'd to document.body, so it's not constrained by
          this panel's stacking context. Milestone awards add a label and
          fly the loop + roll airshow first. */}
      <OttoAward airshow={!!milestoneMsg} show={showPeek} onComplete={() => setShowPeek(false)} label={milestoneMsg} />
      {accentStripe}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-line">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">{eyebrow}</p>
          <h3 className="text-lg font-bold text-ink mb-1">{title}</h3>
          <p className="text-sm text-[#51534A]">{sub}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-xs font-medium text-[#51534A] hover:text-ink underline transition-colors disabled:opacity-40">
          Close
        </button>
      </div>

      {/* Nobody responded (brief §1): no mode choice to offer — the only
          action is Complete without award. Say so plainly instead of showing
          an Award tab that leads to an empty compare screen. */}
      {!showDone && noResponses && (
        <div className="px-6 pt-5">
          <p className="text-xs text-[#51534A]">
            No suppliers responded to this brief. You can close it without an award below.
          </p>
        </div>
      )}

      {/* Mode toggle — hidden on the success step, when nobody responded, and
          on the confirm step (brief §3: once a supplier is chosen the mode is
          locked, so switching can't silently discard the selection). */}
      {!showDone && !noResponses && !isAwardConfirm && (
        <div className="px-6 pt-5">
          <div className="inline-flex items-center bg-white border border-line rounded-xl p-1 gap-1">
            <button
              type="button"
              onClick={() => handleSwitchMode('award')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold tracking-[2px] uppercase transition-colors ${
                mode === 'award'
                  ? 'bg-[#92D400]/15 text-fusion-forest'
                  : 'text-[#51534A] hover:text-ink'
              }`}>
              Award a Supplier
              <span className={`text-[10px] tracking-normal rounded-full px-1.5 py-0.5 ${mode === 'award' ? 'bg-[#92D400]/20 text-fusion-forest' : 'bg-white/[0.06] text-[#51534A]'}`}>
                {respondingPairs.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleSwitchMode('complete')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold tracking-[2px] uppercase transition-colors ${
                mode === 'complete'
                  ? 'bg-[#51534A]/30 text-ink'
                  : 'text-[#51534A] hover:text-ink'
              }`}>
              Complete Without Award
            </button>
          </div>
        </div>
      )}

      {/* Stepper removed (brief §2): the eyebrow "Step X of 2" + the footer
          button already carry progress; the pill pair was a third, ambiguous
          copy of the same thing. */}

      {/* Body */}
      <div className="px-6 pt-5 pb-6">

        {/* ===== AWARD · COMPARE ===== */}
        {isAwardCompare && (
          <>
            {/* Controls — sort + range summary */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-1.5">
                <span className="fusion-field-label mr-1">Sort</span>
                {[
                  { value: 'price', label: 'Price ↑' },
                  { value: 'submitted', label: 'Submitted ↑' },
                  { value: 'name', label: 'A → Z' },
                ].map(o => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setSortBy(o.value)}
                    className={`px-3 py-1 rounded-full border text-[11px] font-bold tracking-[2px] uppercase transition-colors ${
                      sortBy === o.value
                        ? 'bg-[#92D400]/12 border-[#92D400] text-fusion-forest'
                        : 'bg-white border-line text-[#51534A] hover:text-ink'
                    }`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {hasResponses && priceMin != null && (
                <span className="inline-flex items-center gap-1.5 bg-white border border-line rounded-full px-3 py-1 text-[11px] text-fusion-mid-grey">
                  <strong className="text-ink">{respondingPairs.length} responded</strong>
                  <span className="text-[#51534A]">·</span>
                  <span>range</span>
                  <strong className="text-ink">{formatGBPShort(priceMin)}–{formatGBPShort(priceMax)}</strong>
                  <span className="text-[#51534A]">ex-VAT</span>
                </span>
              )}
            </div>

            {!hasResponses ? (
              <div className="bg-white border border-dashed border-line rounded-md px-6 py-8 text-center">
                <p className="text-sm font-semibold text-ink mb-1">No suppliers have responded yet</p>
                <p className="text-xs text-[#51534A]">Switch to "Complete Without Award" if this brief isn't going ahead.</p>
              </div>
            ) : (
              <div className="grid gap-2.5" role="radiogroup" aria-label="Responding suppliers — pick a winner">
                {sortedPairs.map(({ recipient, response }) => {
                  const supplierId = recipient.supplier_id;
                  const isSelected = supplierId === selectedSupplierId;
                  const isCheapest = supplierId === cheapestSupplierId;
                  const cs = recipient.suppliers?.compliance_status;
                  const isBlocked = cs === 'red';
                  const cardBorder = isSelected ? 'border-[#92D400]' : isBlocked ? 'border-[#E0290A]/40' : 'border-line';
                  const cardBg = isSelected ? 'bg-[#92D400]/[0.05]' : 'bg-white';
                  const quoteType = response.is_itemised ? 'Itemised' : 'Lump-sum';
                  const meta = supplierMeta[supplierId] || {};
                  return (
                    <button
                      key={recipient.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={isBlocked}
                      tabIndex={isBlocked ? -1 : (isSelected || (!selectedSupplierId && supplierId === sortedPairs.find(p => p.recipient.suppliers?.compliance_status !== 'red')?.recipient.supplier_id) ? 0 : -1)}
                      onKeyDown={handleCardKeyDown}
                      onClick={() => handleSelectSupplier(supplierId)}
                      disabled={isBlocked}
                      className={`relative w-full text-left border ${cardBorder} ${cardBg} rounded-md px-4 py-4 transition-colors ${
                        isBlocked ? 'cursor-not-allowed opacity-60'
                          : 'hover:border-[#DADCCD] hover:bg-white'
                      } overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#92D400] focus-visible:ring-offset-1`}>
                      {isSelected && <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#92D400]" />}

                      <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center mb-3">
                        <span className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-[#92D400]' : 'border-[#DADCCD]'
                        }`}>
                          {isSelected && <span className="w-2 h-2 rounded-full bg-[#92D400]" />}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-ink truncate">{recipient.suppliers?.supplier_name || 'Supplier'}</span>
                            {/* Superlative tag is neutral, not green (§6: green
                                is reserved for selection). Suppressed when every
                                quote is the same price. */}
                            {hasPriceSpread && isCheapest && (
                              <span className="inline-block bg-[#EEF1E6] text-[#51534A] border border-[#DADCCD] rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase">Lowest price</span>
                            )}
                            {cs === 'red' && (
                              <span className="inline-block bg-[#E0290A]/15 text-[#E0290A] border border-[#E0290A]/40 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase">Blocked · Red</span>
                            )}
                            {/* Recognition badge names itself — Oak status with the
                                acorn/oak mark — instead of an undecodable "Green"
                                pill (brief §2). */}
                            {cs === 'green' && (
                              <span className="inline-flex items-center gap-1 bg-[#EEF1E6] text-fusion-forest border border-[#DADCCD] rounded-full pl-1 pr-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase">
                                <OakStatusBadge frame={5} size={14} animate={false} />
                                Oak status
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-[#51534A] mt-0.5 truncate">
                            {/* Working history is what an operator weighs against
                                price (§2), replacing the internal "Uncategorised". */}
                            {workingHistoryText(meta)}
                            {recipient.suppliers?.main_contact_name ? ` · ${recipient.suppliers.main_contact_name}` : ''}
                          </p>
                          {/* Read-only compliance flag (§2) — never blocks the
                              award, just surfaces documents lapsing soon. Paired
                              with a word, not colour alone (§7). */}
                          {(meta.expiringCount > 0 || cs === 'amber') && (
                            <p className="text-[11px] text-[#7A4E10] mt-0.5 inline-flex items-center gap-1">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                              {meta.expiringCount > 0
                                ? `${meta.expiringCount} document${meta.expiringCount === 1 ? '' : 's'} expiring`
                                : 'Documents need attention'}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-0.5">Total (ex-VAT)</p>
                          <p className="text-lg font-extrabold leading-none text-ink">
                            {response.total_price != null ? formatGBP(response.total_price) : 'Not specified'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-line">
                        <div>
                          <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">Earliest Delivery</p>
                          <p className="text-xs font-semibold text-ink">{dateOrNS(response.deadline_date)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">Payment Schedule</p>
                          <p className="text-xs font-semibold text-ink">{response.payment_schedule ? truncate(response.payment_schedule, 28) : 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">Quote Type</p>
                          <p className="text-xs font-semibold text-ink">{quoteType}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">Submitted</p>
                          <p className="text-xs font-semibold text-ink">{dateOrNS(response.submitted_at || response.created_at)}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ===== AWARD · CONFIRM ===== */}
        {isAwardConfirm && selectedPair && (
          <>
            {/* Locked award summary (brief §3): names the supplier + their quote.
                The mode segment is hidden on this step, so this line + Change is
                the only way back — a change can't be discarded by accident. */}
            <div className="relative bg-[#009159]/[0.06] border border-[#009159]/30 rounded-md px-5 py-4 mb-5 overflow-hidden">
              <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#009159]" />
              <div className="flex items-start justify-between gap-3">
                <p className="text-[10px] font-bold tracking-[2px] uppercase text-[#009159] mb-1.5">Awarding to</p>
                {!singleResponse && (
                  <button type="button" onClick={() => { setError(null); setStep('compare'); }} disabled={submitting}
                    className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#2E8FA1] hover:underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-40">
                    Change
                  </button>
                )}
              </div>
              <p className="text-base font-bold text-ink mb-0.5">{selectedPair.recipient.suppliers?.supplier_name}</p>
              <p className="text-xs text-fusion-mid-grey mb-2">
                {workingHistoryText(supplierMeta[selectedPair.recipient.supplier_id] || {})}
                {selectedPair.recipient.suppliers?.main_contact_name ? ` · ${selectedPair.recipient.suppliers.main_contact_name}` : ''}
              </p>
              <p className="text-xl font-extrabold text-fusion-forest">
                {selectedPair.response.total_price != null ? formatGBP(selectedPair.response.total_price) : 'Not specified'}
                <span className="text-xs text-[#51534A] font-normal ml-2">quoted · ex-VAT</span>
              </p>
            </div>

            <div className="mb-5">
              {/* No asterisk — the amount is prefilled, nothing is missing (§3). */}
              <label className="block text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">Awarded Amount</label>
              <p className="text-xs text-[#51534A] mb-2 leading-relaxed">Prefilled from the quote — adjust if you negotiated a different figure.</p>
              <div className="max-w-[240px]">
                <MoneyInput value={awardAmount} onChange={setAwardAmount} min="0" placeholder="e.g. 1250" />
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] text-[#51534A]">Net (ex-VAT)</span>
                {awardDelta != null && (
                  <span className={`text-[10px] font-bold ${awardDelta > 0 ? 'text-[#7A4E10]' : 'text-[#009159]'}`}>
                    · {formatGBPShort(Math.abs(awardDelta))} {awardDelta > 0 ? 'above' : 'below'} quote
                  </span>
                )}
              </div>
            </div>

            {/* Decision reason — above the emails (§3), the field most valuable
                months later. Kept mandatory (your call): the award button stays
                disabled until it's filled. Renault placeholder removed. */}
            <div className="mb-5">
              <label className="block text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">
                Decision Reason
                <span className="ml-2 inline-flex items-center gap-1 align-middle text-[9px] font-bold uppercase tracking-[0.12em] text-[#51534A] bg-[#F2F4EA] border border-line rounded-full px-1.5 py-0.5 normal-case">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  Internal only
                </span>
                <span className="block text-[#51534A] font-normal tracking-normal normal-case mt-0.5 text-[11px]">Why this supplier — for the Fusion 2K team’s records. Suppliers never see this.</span>
              </label>
              <AutoSizeTextarea
                value={decisionReason}
                onChange={e => setDecisionReason(e.target.value)}
                minRows={3}
                placeholder="e.g. Best price with the fastest lead time, and strong recent work on a similar event."
                className="w-full bg-white border border-line rounded-xl text-sm text-ink px-3.5 py-3 focus:outline-none focus:border-[#92D400]" />
            </div>

            {/* Notifications, grouped (brief §3): Winner + Not awarded are
                ticked/previewable; No response is listed greyed and never
                emailed so "N will be emailed" reconciles against everyone
                invited. */}
            <div className="bg-white border border-line rounded-md px-4 py-3.5 mb-5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest">Notifications</span>
                  <span className="bg-white/[0.05] text-[#51534A] rounded-full px-2 py-0.5 text-[10px]">{tickedEmailCount} will be emailed</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button type="button" onClick={selectAllEmails} disabled={submitting}
                    className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors disabled:opacity-40">Select all</button>
                  <span className="text-[#51534A] text-[10px]">·</span>
                  <button type="button" onClick={clearAllEmails} disabled={submitting}
                    className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors disabled:opacity-40">Clear all</button>
                </div>
              </div>
              <p className="text-[10px] text-[#51534A] mb-2.5 leading-relaxed">Untick anyone you'll contact yourself instead — the brief still moves to Awarded either way.</p>

              {/* Winner */}
              <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">Winner</p>
              <div className="divide-y divide-line mb-2.5">
                {respondingPairs.filter(p => p.recipient.supplier_id === selectedSupplierId).map(({ recipient, response }) => {
                  const isRecordOnly = recipient.suppliers?.is_record_only;
                  return (
                    <label key={recipient.id} className={`flex items-center gap-2.5 py-1.5 text-xs cursor-pointer text-fusion-forest font-semibold ${isRecordOnly ? 'opacity-60 cursor-not-allowed' : ''}`}>
                      <input type="checkbox" checked={emailRecipientIds.has(recipient.id) && !isRecordOnly} disabled={isRecordOnly}
                        onChange={() => toggleEmailRecipient(recipient.id)}
                        className="w-3.5 h-3.5 rounded accent-active-green cursor-pointer flex-shrink-0 disabled:cursor-not-allowed" />
                      <span className="flex-1 truncate">{recipient.suppliers?.supplier_name || 'Supplier'}{isRecordOnly && <span className="text-[#51534A] font-normal ml-2">· record-only</span>}</span>
                      <span className="text-fusion-forest">{response.total_price != null ? formatGBPShort(response.total_price) : 'Not specified'} — Awarded</span>
                    </label>
                  );
                })}
              </div>

              {/* Not awarded */}
              {respondingPairs.some(p => p.recipient.supplier_id !== selectedSupplierId) && (
                <>
                  <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">Not awarded</p>
                  <div className="divide-y divide-line mb-2.5">
                    {respondingPairs.filter(p => p.recipient.supplier_id !== selectedSupplierId).map(({ recipient, response }) => {
                      const isRecordOnly = recipient.suppliers?.is_record_only;
                      return (
                        <label key={recipient.id} className={`flex items-center gap-2.5 py-1.5 text-xs cursor-pointer text-fusion-mid-grey ${isRecordOnly ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          <input type="checkbox" checked={emailRecipientIds.has(recipient.id) && !isRecordOnly} disabled={isRecordOnly}
                            onChange={() => toggleEmailRecipient(recipient.id)}
                            className="w-3.5 h-3.5 rounded accent-active-green cursor-pointer flex-shrink-0 disabled:cursor-not-allowed" />
                          <span className="flex-1 truncate">{recipient.suppliers?.supplier_name || 'Supplier'}{isRecordOnly && <span className="text-[#51534A] font-normal ml-2">· record-only</span>}</span>
                          <span className="text-[#51534A]">{response.total_price != null ? formatGBPShort(response.total_price) : 'Not specified'} — Not selected</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              {/* No response — listed, greyed, never emailed */}
              {awardNonResponders.length > 0 && (
                <>
                  <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-1">No response — not emailed</p>
                  <div className="divide-y divide-line">
                    {awardNonResponders.map(r => (
                      <div key={r.id} className="flex items-center gap-2.5 py-1.5 text-xs text-[#9AA093]">
                        <span className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1 truncate">{r.suppliers?.supplier_name || 'Supplier'}</span>
                        <span>No response</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">
                Message to {firstName(selectedPair.recipient.suppliers?.main_contact_name) || selectedPair.recipient.suppliers?.supplier_name} <span className="text-[#51534A] font-normal tracking-normal normal-case">(optional)</span>
              </label>
              <AutoSizeTextarea
                value={winnerMessage}
                onChange={e => { setWinnerMessage(e.target.value); setWinnerMessageTouched(true); }}
                minRows={4}
                className="w-full bg-white border border-line rounded-xl text-sm text-ink px-3.5 py-3 focus:outline-none focus:border-[#92D400]" />
            </div>

            {/* Not-awarded email (brief §4): editable body, locked structure
                (subject / greeting / sign-off stay fixed in the template). Tabs
                let one message cover everyone or be overridden per supplier.
                Edits apply to THIS brief only — Settings owns the standard. */}
            {notSelectedResponders.length > 0 && (
              <div className="mb-4">
                <label className="block text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">Not-awarded email</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <button type="button" onClick={() => setNotSelectedTab('all')}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${notSelectedTab === 'all' ? 'border-[#92D400] text-fusion-forest bg-[#92D400]/10' : 'border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}>
                    All suppliers
                  </button>
                  {notSelectedResponders.map(p => {
                    const sid = p.recipient.supplier_id;
                    const overridden = notSelectedOverrides[sid] != null;
                    return (
                      <button key={sid} type="button" onClick={() => setNotSelectedTab(sid)}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${notSelectedTab === sid ? 'border-[#92D400] text-fusion-forest bg-[#92D400]/10' : 'border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}>
                        {p.recipient.suppliers?.supplier_name || 'Supplier'}
                        {overridden && <span className="w-1.5 h-1.5 rounded-full bg-[#92D400]" aria-label="custom message" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#51534A] mb-2 leading-relaxed">
                  {notSelectedTab === 'all'
                    ? 'One message for everyone not awarded. Override an individual with their tab above.'
                    : <>Custom message for <strong className="text-ink">{activeSupplierName}</strong> only{activeOverridden ? '' : ' — starts from the all-suppliers message'}.</>}
                </p>
                <AutoSizeTextarea
                  value={activeNotSelectedBody}
                  onChange={e => setActiveNotSelectedBody(e.target.value)}
                  minRows={4}
                  aria-label={notSelectedTab === 'all' ? 'Not-awarded message for all suppliers' : `Not-awarded message for ${activeSupplierName}`}
                  className="w-full bg-white border border-line rounded-xl text-sm text-ink px-3.5 py-3 focus:outline-none focus:border-[#92D400]" />
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#51534A] mr-1">Insert reason</span>
                  {DECLINE_SNIPPETS.map(s => (
                    <button key={s.key} type="button" onClick={() => appendSnippet(s.text)}
                      className="text-[11px] font-semibold px-2 py-1 rounded-full border border-line text-fusion-mid-grey hover:border-[#92D400] hover:text-fusion-forest transition-colors">
                      + {s.label}
                    </button>
                  ))}
                </div>
                <div aria-live="polite">
                  {activeRisky.length > 0 && (
                    <p className="mt-2 text-[11px] text-[#7A4E10] bg-[#E89A2E]/10 border border-[#E89A2E]/30 rounded-lg px-3 py-2 leading-relaxed">
                      Heads up — this message mentions {activeRisky.join(' and ')}. These emails go to suppliers who bid against each other; you can still send it if that’s intentional.
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <p className="text-[10px] text-[#51534A] leading-snug">Edits apply to this brief only. The standard wording lives in Settings.</p>
                  <button type="button" onClick={resetActiveNotSelected}
                    className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-[#E0290A] transition-colors flex-shrink-0">
                    Reset to standard
                  </button>
                </div>
              </div>
            )}

            {/* Consequence line (brief §3/§7) — plain language, tied to the
                footer action via id (aria-describedby) so it's read before
                activation. */}
            <div id="award-consequence" className="bg-[#F7F9F0] border border-line rounded-md px-4 py-3 text-[11px] text-[#51534A] leading-relaxed">
              Pressing <strong className="text-ink">Award</strong> awards{' '}
              <strong className="text-ink">{selectedPair.recipient.suppliers?.supplier_name}</strong>{' '}
              <strong className="text-ink">{awardAmount && !isNaN(Number(awardAmount)) ? formatGBP(Number(awardAmount)) : 'the amount above'}</strong> (ex-VAT),{' '}
              {tickedEmailCount === 0
                ? 'sends no emails'
                : `sends ${tickedEmailCount} notification email${tickedEmailCount === 1 ? '' : 's'}`}
              , and moves this brief to <strong className="text-ink">Awarded</strong>. You’ll get <strong className="text-ink">30 seconds to cancel</strong> before anything is written or sent.
            </div>
          </>
        )}

        {/* ===== COMPLETE WITHOUT AWARD ===== */}
        {mode === 'complete' && !showDone && (
          <>
            <div className="mb-5">
              <p className="text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">
                Why is this brief being completed without award? *
              </p>
              <p className="text-xs text-[#51534A] mb-3 leading-relaxed">Helps the team understand the decision later and improves how we match future briefs.</p>
              <div className="grid grid-cols-2 gap-2">
                {REASON_OPTIONS.map(opt => {
                  const isSelected = completeReason === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setCompleteReason(opt.key)}
                      className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-[#E89A2E]/[0.06] border border-[#E89A2E] text-ink'
                          : 'bg-white border border-line text-ink hover:border-[#DADCCD]'
                      }`}>
                      <span className={`w-3.5 h-3.5 rounded-full border-[1.5px] flex-shrink-0 flex items-center justify-center ${
                        isSelected ? 'border-[#E89A2E]' : 'border-[#DADCCD]'
                      }`}>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-[#E89A2E]" />}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {completeReason === 'other' && (
                <input
                  type="text"
                  value={completeReasonOther}
                  onChange={e => setCompleteReasonOther(e.target.value)}
                  placeholder="Briefly describe the reason"
                  className="mt-2.5 w-full bg-white border border-line rounded-xl text-sm text-ink px-3.5 py-2.5 focus:outline-none focus:border-[#E89A2E]" />
              )}
            </div>

            <div className="mb-5">
              <label className="block text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">
                Notes <span className="text-[#51534A] font-normal tracking-normal normal-case">(optional)</span>
              </label>
              <AutoSizeTextarea
                value={completeNotes}
                onChange={e => setCompleteNotes(e.target.value)}
                minRows={3}
                placeholder="Anything the team should know — context, lessons learned, suppliers to revisit next time…"
                className="w-full bg-white border border-line rounded-xl text-sm text-ink px-3.5 py-3 focus:outline-none focus:border-[#92D400]" />
            </div>

            {closeNotifyPairs.length > 0 && (
              <div className="bg-white border border-line rounded-md px-4 py-3.5 mb-5">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest">Notifications</span>
                    <span className="bg-white/[0.05] text-[#51534A] rounded-full px-2 py-0.5 text-[10px]">
                      {emailRecipientIds.size} of {closeNotifyPairs.length} will be emailed
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={selectAllEmails}
                      disabled={submitting}
                      className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors disabled:opacity-40">
                      Select all
                    </button>
                    <span className="text-[#51534A] text-[10px]">·</span>
                    <button
                      type="button"
                      onClick={clearAllEmails}
                      disabled={submitting}
                      className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors disabled:opacity-40">
                      Clear all
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-[#51534A] mb-2.5 leading-relaxed">Responders default to ticked; non-responders default off. Untick anyone you'll contact yourself — the brief still closes either way.</p>
                <div className="divide-y divide-line">
                  {closeNotifyPairs.map(({ recipient, response }) => {
                    const willEmail = emailRecipientIds.has(recipient.id);
                    const isRecordOnly = recipient.suppliers?.is_record_only;
                    const isResponder = recipient.status === 'responded' && !!response;
                    return (
                      <label
                        key={recipient.id}
                        className={`flex items-center gap-2.5 py-1.5 text-xs text-fusion-mid-grey cursor-pointer ${isRecordOnly ? 'opacity-60 cursor-not-allowed' : ''}`}>
                        <input
                          type="checkbox"
                          checked={willEmail && !isRecordOnly}
                          disabled={isRecordOnly}
                          onChange={() => toggleEmailRecipient(recipient.id)}
                          className="w-3.5 h-3.5 rounded accent-active-green cursor-pointer flex-shrink-0 disabled:cursor-not-allowed" />
                        <span className="flex-1 truncate">
                          {recipient.suppliers?.supplier_name || 'Supplier'}
                          {isRecordOnly && <span className="text-[#51534A] font-normal ml-2">· record-only</span>}
                        </span>
                        <span className={isResponder ? 'text-fusion-forest' : 'text-[#51534A]'}>
                          {isResponder ? 'Quote received' : recipient.status === 'accepted' ? 'Accepted' : 'No response'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">
                Message to all suppliers <span className="text-[#51534A] font-normal tracking-normal normal-case">(optional)</span>
              </label>
              <p className="text-[11px] text-[#51534A] mb-2 leading-relaxed">
                Adds your own note above the default close message below. Leave blank to send just the default on its own.
              </p>
              <AutoSizeTextarea
                value={closedMessage}
                onChange={e => setClosedMessage(e.target.value)}
                minRows={3}
                placeholder="Your own note — optional."
                className="w-full bg-white border border-line rounded-xl text-sm text-ink px-3.5 py-3 focus:outline-none focus:border-[#92D400]" />
              {/* Default close preview — shows the prose every supplier reads
                  when the brief is closed, so the admin knows exactly what's
                  sent (with or without a personal note above). Same string
                  briefClosedEmail uses, imported from lib/email.js. */}
              <div className="mt-2.5 px-3.5 py-3 bg-white border border-dashed border-line rounded-xl">
                <p className="fusion-field-label">Default close message</p>
                <p className="text-xs text-fusion-mid-grey leading-relaxed italic">{BRIEF_CLOSE_DEFAULT_BODY}</p>
              </div>
            </div>
          </>
        )}

        {/* ===== DONE / SUCCESS ===== */}
        {showDone && (
          <div className="text-center py-6">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full border-2 flex items-center justify-center ${
              mode === 'award'
                ? 'bg-[#92D400]/[0.12] border-[#92D400] text-fusion-forest'
                : 'bg-[#E89A2E]/[0.12] border-[#E89A2E] text-[#7A4E10]'
            }`}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h4 className="text-lg font-bold text-ink mb-2">{title}</h4>
            <p className="text-xs text-fusion-mid-grey leading-relaxed max-w-md mx-auto mb-4">
              {mode === 'award' ? (
                <>
                  <strong className="text-ink">{formatGBP(Number(awardAmount) || 0)}</strong> (ex-VAT) awarded.{' '}
                  {lastActionNotified ? (
                    <>All {respondingPairs.length} suppliers have been notified, and this brief has moved to <strong className="text-ink">Awarded</strong>.</>
                  ) : (
                    <>This brief has moved to <strong className="text-ink">Awarded</strong>. Suppliers haven't been notified yet — let them know whenever you're ready.</>
                  )}
                </>
              ) : (
                <>
                  This brief has moved to <strong className="text-ink">Closed</strong>.{' '}
                  {respondingPairs.length === 0 ? '' : lastActionNotified
                    ? `${respondingPairs.length} responding supplier${respondingPairs.length === 1 ? '' : 's'} ${respondingPairs.length === 1 ? 'has' : 'have'} been told.`
                    : `Suppliers haven't been notified yet — let them know whenever you're ready.`}
                </>
              )}
            </p>
            {error && (
              <p className="text-[11px] text-[#E0290A] bg-[#E0290A]/[0.08] border border-[#E0290A]/30 rounded-xl px-3 py-2 max-w-md mx-auto">{error}</p>
            )}
          </div>
        )}
      </div>

      {/* Footer — hidden on Done */}
      {!showDone && (
        <div className="border-t border-line bg-[#F7F9F0] px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-[#51534A] min-w-0 flex-1">
            {error ? (
              <span className="text-[#E0290A]">{error}</span>
            ) : isAwardCompare && selectedPair ? (
              <span>
                <strong className="text-ink">{selectedPair.recipient.suppliers?.supplier_name} selected</strong>
                {selectedPair.response.total_price != null && (
                  <> · {formatGBPShort(selectedPair.response.total_price)} ex-VAT</>
                )}
                {cheapestDelta != null && cheapestDelta > 0 && (
                  <> · cheapest by {formatGBPShort(cheapestDelta)}</>
                )}
              </span>
            ) : isAwardCompare ? (
              <span>Pick a supplier to continue.</span>
            ) : isAwardConfirm ? (
              <span>Both options mark the brief as <strong className="text-ink">Awarded</strong>. Only the green button sends emails right now.</span>
            ) : mode === 'complete' && completeReason ? (
              <span><strong className="text-ink">Reason:</strong> {completeReason === 'other' ? (completeReasonOther.trim() || 'Other') : REASON_LABEL_FOR(completeReason)}</span>
            ) : (
              <span>Pick a reason to continue.</span>
            )}
          </div>
          <div className="flex gap-2.5 flex-shrink-0">
            {isAwardCompare && (
              <>
                <button type="button" onClick={onClose} disabled={submitting}
                  className="bg-white border border-line text-ink text-[13px] font-semibold rounded-xl px-4 py-2.5 hover:border-[#DADCCD] transition-colors disabled:opacity-40">
                  Cancel
                </button>
                <button type="button"
                  onClick={() => { setError(null); setStep('confirm'); }}
                  disabled={!selectedPair || isWinnerRed || submitting}
                  className="bg-[#92D400] text-[#131313] text-[13px] font-bold rounded-xl px-5 py-2.5 hover:bg-[#a4e810] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  Continue →
                </button>
              </>
            )}
            {isAwardConfirm && (() => {
              // Single primary button whose label reflects the Notifications
              // card selection above. Replaces the old "Award without notifying"
              // / "Award & Notify Suppliers" two-button row — the per-supplier
              // picker already lets the operator skip anyone they want to
              // contact themselves, so a separate "no emails at all" path is
              // redundant. When the operator clears every tick, the button
              // morphs to "Award Brief (no emails)" and the fan-out is skipped.
              // The button names its own action (brief §3): "Award and send N
              // emails", or "Award without emailing" when every tick is cleared.
              const awardLabel = hold
                ? `Awarding in ${hold.seconds}s…`
                : submitting
                  ? 'Sending…'
                  : tickedEmailCount === 0
                    ? 'Award without emailing'
                    : `Award and send ${tickedEmailCount} email${tickedEmailCount === 1 ? '' : 's'}`;
              return (
                <>
                  {/* No compare step to return to on the single-response screen. */}
                  {singleResponse ? (
                    <button type="button" onClick={onClose} disabled={submitting}
                      className="bg-white border border-line text-ink text-[13px] font-semibold rounded-xl px-4 py-2.5 hover:border-[#DADCCD] transition-colors disabled:opacity-40">
                      Cancel
                    </button>
                  ) : (
                    <button type="button" onClick={() => { setError(null); setStep('compare'); }} disabled={submitting}
                      className="bg-white border border-line text-ink text-[13px] font-semibold rounded-xl px-4 py-2.5 hover:border-[#DADCCD] transition-colors disabled:opacity-40">
                      ← Back
                    </button>
                  )}
                  <button type="button" aria-describedby="award-consequence" onClick={(e) => handleSubmitAward(e, { notify: tickedEmailCount > 0 })} disabled={submitting || !!hold}
                    className="bg-[#92D400] text-[#131313] text-[13px] font-bold rounded-xl px-5 py-2.5 hover:bg-[#a4e810] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {awardLabel}
                  </button>
                </>
              );
            })()}
            {mode === 'complete' && (
              <>
                <button type="button" onClick={onClose} disabled={submitting}
                  className="bg-white border border-line text-ink text-[13px] font-semibold rounded-xl px-4 py-2.5 hover:border-[#DADCCD] transition-colors disabled:opacity-40">
                  Cancel
                </button>
                {respondingPairs.length > 0 ? (
                  (() => {
                    // Same dynamic-label pattern as the award flow above.
                    // The Notifications card is the per-supplier picker;
                    // a zero-tick state becomes the "no emails" branch.
                    const closeLabel = submitting
                      ? 'Sending…'
                      : tickedEmailCount === 0
                        ? 'Close Brief (no emails)'
                        : tickedEmailCount === 1
                          ? 'Close Brief & Notify 1 Supplier'
                          : `Close Brief & Notify ${tickedEmailCount} Suppliers`;
                    return (
                      <button type="button" onClick={() => handleSubmitClose({ notify: tickedEmailCount > 0 })}
                        disabled={!completeReason || submitting || (completeReason === 'other' && !completeReasonOther.trim())}
                        className="bg-[#E89A2E] text-[#131313] text-[13px] font-bold rounded-xl px-5 py-2.5 hover:bg-[#f0a73c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        {closeLabel}
                      </button>
                    );
                  })()
                ) : (
                  // No responding suppliers, so there's nothing to fan
                  // out. Skip the email path entirely and just close.
                  <button type="button" onClick={() => handleSubmitClose({ notify: false })}
                    disabled={!completeReason || submitting || (completeReason === 'other' && !completeReasonOther.trim())}
                    className="bg-[#E89A2E] text-[#131313] text-[13px] font-bold rounded-xl px-5 py-2.5 hover:bg-[#f0a73c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {submitting ? 'Sending…' : 'Close Brief'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Exclusive-brief award confirmation (2026-06): brand-styled
          ConfirmDialog instead of window.confirm. Pending state
          carries the deferred notify + burstOrigin captured at the
          click site, so Confirm fires performAward exactly the
          same way the direct path would. */}
      <ConfirmDialog
        open={!!exclusiveAwardPending}
        title="Notify the other suppliers?"
        body={
          exclusiveAwardPending
            ? `This brief was flagged Exclusive, but ${exclusiveAwardPending.otherCount} other supplier${exclusiveAwardPending.otherCount === 1 ? ' is' : 's are'} on it. "Notify everyone" sends the standard "didn't win" email to ${exclusiveAwardPending.otherCount === 1 ? 'them' : 'all of them'}; "Winner only" emails just the winner and leaves the others un-notified.`
            : ''
        }
        confirmLabel="Notify everyone"
        cancelLabel="Cancel"
        tone="primary"
        extraAction={{
          label: 'Winner only',
          onClick: () => {
            const pending = exclusiveAwardPending;
            setExclusiveAwardPending(null);
            // Narrow the notify set to just the winner so the fan-out (read
            // fresh at fire time, thanks to the per-second hold re-render)
            // emails only them — the other suppliers get nothing.
            const winnerRec = respondingPairs.find(
              (p) => p.recipient.supplier_id === selectedSupplierId
            )?.recipient;
            if (winnerRec) setEmailRecipientIds(new Set([winnerRec.id]));
            if (pending) startAwardHold({ notify: true, burstOrigin: pending.burstOrigin });
          },
        }}
        onConfirm={() => {
          const pending = exclusiveAwardPending;
          setExclusiveAwardPending(null);
          if (pending) startAwardHold({ notify: pending.notify, burstOrigin: pending.burstOrigin });
        }}
        onCancel={() => setExclusiveAwardPending(null)}
      />

      {/* 30-second hold bar (brief §9.1) — a fixed snackbar with a live
          countdown. Cancel aborts with nothing written; Send now fires
          immediately. Nothing is committed until this resolves. */}
      {hold && (
        <div
          role="status"
          aria-live="assertive"
          className="fixed right-6 bottom-6 z-[120] bg-[#131313] text-white rounded-xl shadow-lift px-4 py-3 flex items-center gap-4 max-w-[92vw]">
          <div className="min-w-0">
            <p className="font-['Metropolis'] text-[13px] font-bold">
              Awarding {selectedPair?.recipient?.suppliers?.supplier_name || 'supplier'} in {hold.seconds}s
            </p>
            <p className="font-['Metropolis'] text-[11px] text-white/70 truncate">
              {hold.notify
                ? `${tickedEmailCount} email${tickedEmailCount === 1 ? '' : 's'} will send`
                : 'No emails will send'} · Cancel to undo.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button type="button" onClick={cancelHold}
              className="font-['Metropolis'] text-[12px] font-semibold text-white/80 hover:text-white underline">
              Cancel
            </button>
            <button type="button" onClick={sendHoldNow}
              className="bg-[#92D400] text-[#131313] font-['Metropolis'] text-[12px] font-bold rounded-lg px-3 py-1.5 hover:bg-[#a4e810] transition-colors">
              Send now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
